import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Select,
} from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { EmailIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import loiService from 'services/Recruit/services/loi';
import documentVerificationService from 'services/Recruit/services/documentVerification';
import TextEditor from 'common/components/textEditor/TextEditor';
import { getApi } from 'common/services/api';
import { API_PATHS } from 'common/services/apiPaths';
import emailTemplateService from 'services/Recruit/services/emailTemplate';
import { HasAccess } from 'common/utils/accessUtils';
import { accessModuleTitles } from 'common/constant';
import { getStatusBadgeStyle } from 'common/utils/statusColor';

const LOITab = ({ candidateId, candidateName, candidateEmail, applicationId: propApplicationId, isReadOnly = false }) => {
  // Get dynamic permissions
  const [effectivePermission] = HasAccess([accessModuleTitles.Applications]);
  // Support both candidateId and applicationId for backward compatibility
  const [applicationId, setApplicationId] = useState(propApplicationId);
  const [loi, setLOI] = useState(null);
  const [docVerification, setDocVerification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingLOI, setIsSendingLOI] = useState(false);
  const toast = useToast();

  // Check if user is a reviewer based on role
  const { isReviewer, isSuperAdmin } = useMemo(() => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        return { isReviewer: false, isSuperAdmin: false };
      }
      const user = JSON.parse(userData);

      // Check if activeRoleId is populated (has roleName property)
      let role = user?.activeRoleId?.roleName;

      // If activeRoleId is just an ID string, look it up in roles array
      if (!role && user?.activeRoleId && user?.roles) {
        const activeRoleId = typeof user.activeRoleId === 'string'
          ? user.activeRoleId
          : user.activeRoleId._id || user.activeRoleId;


        const activeRole = user.roles.find((r) => {
          const roleId = typeof r === 'object' ? (r._id || r) : r;
          const match = roleId?.toString() === activeRoleId?.toString();
          return match;
        });

        role = activeRole?.roleName;
      }

      // Fallback to user.role if still not found
      if (!role) {
        role = user?.role;
      }

      return {
        isReviewer: role === 'reviewer',
        isSuperAdmin: role === 'superAdmin' || role === 'admin'
      };
    } catch (error) {
      console.error('[LOITab] Error getting user role:', error);
      return { isReviewer: false, isSuperAdmin: false };
    }
  }, []);

  // Check if flow is completed (LOI accepted)
  const isFlowCompleted = loi && loi.status === 'accepted';

  // Disable actions based on permissions and status
  // Use dynamic permissions instead of hardcoded role checks
  const shouldDisableActions = isFlowCompleted || (isReviewer && !isSuperAdmin) || (isReadOnly && !effectivePermission?.update);

  // Use useDisclosure like other modules
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isSendConfirmOpen, onOpen: onSendConfirmOpen, onClose: onSendConfirmClose } = useDisclosure();
  const { isOpen: isWithdrawConfirmOpen, onOpen: onWithdrawConfirmOpen, onClose: onWithdrawConfirmClose } = useDisclosure();

  const [loiForm, setLoiForm] = useState({
    subject: `Letter of Intent - ${candidateName || 'Candidate'}`,
    content: '',
    emailTemplateId: '',
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [applicationData, setApplicationData] = useState(null);

  // Find application from candidateId if not provided
  useEffect(() => {
    const findApplication = async () => {
      if (propApplicationId) {
        setApplicationId(propApplicationId);
        return;
      }

      if (candidateId && !propApplicationId) {
        try {
          // Ensure candidateId is a string
          const candidateIdStr = typeof candidateId === 'string' ? candidateId : (candidateId?._id || candidateId?.toString() || String(candidateId));
          if (!candidateIdStr || candidateIdStr === '[object Object]') {
            console.error('Invalid candidateId:', candidateId);
            return;
          }
          // Get the latest application for this candidate
          const response = await getApi(`${API_PATHS.CANDIDATE_LIST}${candidateIdStr}/applications`);
          const applications = response?.data?.data || [];
          if (applications.length > 0) {
            // Use the most recent application
            const latestApp = applications.sort((a, b) =>
              new Date(b.createdAt) - new Date(a.createdAt)
            )[0];
            setApplicationId(latestApp._id);
          }
        } catch (error) {
          console.error('Error fetching applications for candidate:', error);
        }
      }
    };

    findApplication();
  }, [candidateId, propApplicationId]);

  useEffect(() => {
    if (applicationId) {
      fetchLOI();
      checkDocumentVerification();
      fetchApplicationData();
    } else {
      setLOI(null);
      setDocVerification(null);
      setIsLoading(false);
      setApplicationData(null);
    }
  }, [applicationId]);

  // Fetch email templates on component mount (skip for reviewers)
  useEffect(() => {
    if (!isReviewer) {
      fetchEmailTemplates();
    }
  }, [isReviewer]);

  const fetchEmailTemplates = async () => {
    setIsTemplatesLoading(true);
    try {
      const response = await emailTemplateService.getTemplates({ allData: true, isActive: true });
      // Handle both response structures: { data: { data: [...], metadata: {...} } } or { data: [...], metadata: {...} }
      const templates = response?.data?.data ?? response?.data ?? [];
      setEmailTemplates(Array.isArray(templates) ? templates : []);
    } catch (error) {
      console.error('[LOITab] Error fetching email templates:', error);
      setEmailTemplates([]); // Set empty array on error
      // Don't show error toast, just log it (same as document verification)
    } finally {
      setIsTemplatesLoading(false);
    }
  };

  const fetchApplicationData = async () => {
    if (!applicationId) return;

    try {
      const response = await getApi(`${API_PATHS.RECRUITMENT_APPLICATION}${applicationId}`);
      setApplicationData(response?.data?.data);
    } catch (error) {
      console.error('Error fetching application data:', error);
    }
  };

  useEffect(() => {
    if (loi) {
      setLoiForm({
        subject: loi.subject,
        content: loi.content,
      });
    }
  }, [loi]);

  const fetchLOI = async () => {
    if (!applicationId) return;

    setIsLoading(true);
    try {
      const response = await loiService.getLOI(applicationId);
      setLOI(response.data?.data);
    } catch (error) {
      // 404 is expected if LOI doesn't exist yet
      if (error.response?.status === 404) {
        setLOI(null);
      } else {
        console.error('Error fetching LOI:', error);
        // Only show toast for non-404 errors
        if (error.response?.status !== 404) {
          toast({
            title: 'Error',
            description: error.response?.data?.message || 'Failed to fetch LOI',
            status: 'error',
            duration: 3000,
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const checkDocumentVerification = async () => {
    if (!applicationId) return;

    try {
      const response = await documentVerificationService.getDocumentVerification(applicationId);
      setDocVerification(response.data?.data);
    } catch (error) {
      // Document verification might not exist yet - silently handle 404
      if (error.response?.status !== 404) {
        console.error('Error checking document verification:', error);
      }
      setDocVerification(null);
    }
  };

  const handleCreateLOI = async () => {
    if (!applicationId) {
      toast({
        title: 'Error',
        description: 'Application ID is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!loiForm.subject || !loiForm.content) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await loiService.createLOI({
        applicationId,
        subject: loiForm.subject,
        content: loiForm.content,
      });

      toast({
        title: 'Success',
        description: response?.data?.message || 'LOI created successfully',
        status: 'success',
        duration: 3000,
      });
      onCreateClose();
      // Reset form
      setLoiForm({
        subject: `Letter of Intent - ${candidateName || 'Candidate'}`,
        content: '',
        emailTemplateId: '',
      });
      fetchLOI();
    } catch (error) {
      console.error('Error creating LOI:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to create LOI',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLOI = async () => {
    if (!loiForm.subject || !loiForm.content) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      await loiService.updateLOI(loi._id, {
        subject: loiForm.subject,
        content: loiForm.content,
      });
      toast({
        title: 'Success',
        description: 'LOI updated successfully',
        status: 'success',
        duration: 3000,
      });
      onEditClose();
      fetchLOI();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update LOI',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleSendLOIClick = () => {
    onSendConfirmOpen();
  };

  const handleSendLOI = async () => {
    setIsSendingLOI(true);

    try {
      await loiService.sendLOI(loi._id);
      onSendConfirmClose(); // Close modal only on success
      toast({
        title: 'Success',
        description: 'LOI sent successfully',
        status: 'success',
        duration: 3000,
      });
      fetchLOI();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to send LOI',
        status: 'error',
        duration: 3000,
      });
      // Don't close modal on error so user can try again
    } finally {
      setIsSendingLOI(false);
    }
  };

  const handleWithdrawLOIClick = () => {
    onWithdrawConfirmOpen();
  };

  const handleWithdrawLOI = async () => {
    onWithdrawConfirmClose();

    try {
      await loiService.withdrawLOI(loi._id);
      toast({
        title: 'Success',
        description: 'LOI withdrawn successfully',
        status: 'success',
        duration: 3000,
      });
      fetchLOI();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to withdraw LOI',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Helper function to replace placeholders in content
  const replacePlaceholders = (content, candidateName, jobRole) => {
    if (!content) return '';

    const placeholderMap = {
      '[Candidate Name]': candidateName || '[Candidate Name]',
      '[CANDIDATE_NAME]': candidateName || '[Candidate Name]',
      '{{CANDIDATE_NAME}}': candidateName || '[Candidate Name]',
      '[YourName]': candidateName || '[YourName]',
      '[YOUR_NAME]': candidateName || '[YourName]',
      '{{YOUR_NAME}}': candidateName || '[YourName]',
      '[Job Role]': jobRole || '[Job Role]',
      '[JOB_ROLE]': jobRole || '[Job Role]',
      '{{JOB_TITLE}}': jobRole || '[Job Role]',
      '{{JOB_ROLE}}': jobRole || '[Job Role]',
    };

    let replacedContent = content;
    Object.entries(placeholderMap).forEach(([placeholder, value]) => {
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      replacedContent = replacedContent.replace(regex, value);
    });

    return replacedContent;
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      setLoiForm({
        ...loiForm,
        emailTemplateId: '',
        subject: `Letter of Intent - ${candidateName || 'Candidate'}`,
        content: '',
      });
      return;
    }

    const template = emailTemplates.find(t => t._id === templateId);
    if (template) {
      // Get candidate name and job role from application data or props
      const candidateNameValue = candidateName || applicationData?.candidateDetails?.name || applicationData?.candidate?.name || '';
      const jobRole = applicationData?.job?.postingTitle || '';

      // Warn if template is selected but we don't have application data yet
      if (!applicationData || (!candidateNameValue && !jobRole)) {
        toast({
          title: 'Info',
          description: 'Application data is loading. Placeholders will be replaced once data is available.',
          status: 'info',
          duration: 3000,
        });
      }

      // Replace placeholders in template content
      const replacedContent = replacePlaceholders(
        template.content || '',
        candidateNameValue,
        jobRole
      );

      const replacedSubject = replacePlaceholders(
        template.subject || `Letter of Intent - ${candidateNameValue || 'Candidate'}`,
        candidateNameValue,
        jobRole
      );

      setLoiForm({
        ...loiForm,
        emailTemplateId: templateId,
        subject: replacedSubject,
        content: replacedContent,
      });

      if (candidateNameValue && jobRole) {
        toast({
          title: 'Success',
          description: 'Email template applied with placeholders replaced',
          status: 'success',
          duration: 3000,
        });
      } else {
        toast({
          title: 'Info',
          description: 'Email template applied. Placeholders will be replaced when application data is available.',
          status: 'info',
          duration: 3000,
        });
      }
    }
  };

  const canCreateLOI = () => {
    // Reviewers cannot create LOI, only view (unless they're superAdmin)
    if (isReviewer && !isSuperAdmin) {
      return false;
    }
    const result = docVerification && docVerification.verificationStatus === 'approved';
    return result;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft' },
      sent: { label: 'Sent' },
      viewed: { label: 'Viewed' },
      accepted: { label: 'Accepted' },
      rejected: { label: 'Rejected' },
      on_hold: { label: 'On Hold' },
      withdrawn: { label: 'Withdrawn' },
    };

    const config = statusConfig[status] || { label: status };
    const badgeStyle = getStatusBadgeStyle(status);
    return (
      <HStack spacing={2}>
        <Box bg={badgeStyle.color} h="8px" w="8px" borderRadius="full" />
        <Text as="span" color={badgeStyle.color} style={{ color: badgeStyle.color }} fontWeight="600">
          {config.label}
        </Text>
      </HStack>
    );
  };

  if (!applicationId) {
    return (
      <Box p={4} textAlign="center">
        <Text color="gray.500">Please select an application first.</Text>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box p={4}>
      {/* Read-only banner when flow is completed */}
      {isFlowCompleted && (
        <Alert status="success" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            LOI has been accepted by the candidate. This record is now read-only.
          </AlertDescription>
        </Alert>
      )}

      {/* Read-only banner for reviewers (except superAdmin) */}
      {isReviewer && !isSuperAdmin && !isFlowCompleted && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            You have read-only access to LOI information. You cannot create or modify LOIs.
          </AlertDescription>
        </Alert>
      )}

      <HStack justify="space-between" mb={4}>
        <Text fontSize="xl" fontWeight="bold">
          Letter of Intent (LOI) - {candidateName || 'Candidate'}
        </Text>
        <HStack>
          {(() => {
            const shouldShowButton = !loi && canCreateLOI() && !shouldDisableActions && effectivePermission?.create;
            return shouldShowButton ? (
              <Button colorScheme="blue" onClick={onCreateOpen}>
                Create LOI
              </Button>
            ) : null;
          })()}
          {!loi && !canCreateLOI() && !(isReviewer && !isSuperAdmin) && (
            <Text fontSize="sm" color="gray.500">
              Document verification must be approved first
            </Text>
          )}
          {loi && loi.status === 'draft' && !shouldDisableActions && effectivePermission?.update && (
            <>
              <Button colorScheme="green" onClick={onEditOpen}>
                Edit LOI
              </Button>
              <Button colorScheme="blue" onClick={handleSendLOIClick} leftIcon={<EmailIcon />}>
                Send LOI
              </Button>
            </>
          )}
          {loi && (loi.status === 'sent' || loi.status === 'viewed') && !shouldDisableActions && effectivePermission?.update && (
            <Button colorScheme="red" onClick={handleWithdrawLOIClick}>
              Withdraw LOI
            </Button>
          )}
        </HStack>
      </HStack>

      {!loi ? (
        <Box p={8} textAlign="center" border="1px dashed" borderColor="gray.300" borderRadius="md">
          {!canCreateLOI() ? (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                LOI not created yet.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Text color="gray.500">No LOI created yet.</Text>
              <Text color="gray.500" fontSize="sm" mt={2}>
                Click "Create LOI" to create a new Letter of Intent.
              </Text>
            </>
          )}
        </Box>
      ) : (
        <VStack align="stretch" spacing={4}>
          {/* Status Card */}
          <Box p={4} bg="gray.50" borderRadius="md">
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="bold">LOI Status:</Text>
              {getStatusBadge(loi.status)}
            </HStack>
            {loi.sentAt && (
              <Text fontSize="sm" color="gray.600">
                Sent at: {new Date(loi.sentAt).toLocaleString()}
              </Text>
            )}
            {loi.viewedAt && (
              <Text fontSize="sm" color="gray.600">
                Viewed at: {new Date(loi.viewedAt).toLocaleString()}
              </Text>
            )}
            {loi.respondedAt && (
              <Text fontSize="sm" color="gray.600">
                Responded at: {new Date(loi.respondedAt).toLocaleString()}
              </Text>
            )}
            {loi.candidateResponse && (
              <Box mt={2} p={2} bg="white" borderRadius="md">
                <Text fontSize="sm" fontWeight="medium">
                  Candidate Response: {loi.candidateResponse.status}
                </Text>
                {loi.candidateResponse.message && (
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {loi.candidateResponse.message}
                  </Text>
                )}
              </Box>
            )}
          </Box>

          {/* LOI Content Preview */}
          <Box p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
            <Text fontWeight="bold" mb={2}>
              Subject: {loi.subject}
            </Text>
            <Box
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(loi.content) }}
              className="loi-content-preview"
            />
          </Box>

          {/* Action Buttons */}
          {loi.status === 'draft' && !shouldDisableActions && effectivePermission?.update && (
            <HStack>
              <Button onClick={onEditOpen}>Edit</Button>
              <Button colorScheme="blue" onClick={onPreviewOpen}>
                Preview
              </Button>
            </HStack>
          )}
        </VStack>
      )}

      {/* Create LOI Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Letter of Intent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Email Template (Optional)</FormLabel>
                <Select
                  placeholder={isTemplatesLoading ? "Loading templates..." : "Select an email template"}
                  value={loiForm.emailTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  isDisabled={isTemplatesLoading}
                >
                  <option value="">None - Use custom content</option>
                  {emailTemplates && emailTemplates.length > 0 ? (
                    emailTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {template.name} {template.category ? `(${template.category})` : ''}
                      </option>
                    ))
                  ) : (
                    !isTemplatesLoading && <option value="" disabled>No templates available</option>
                  )}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {isTemplatesLoading
                    ? "Loading templates..."
                    : emailTemplates.length > 0
                      ? `Selecting a template will auto-fill subject and content with placeholders replaced (${emailTemplates.length} templates available)`
                      : "No email templates available. Create templates in the Email Templates section."}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel>
                  Subject <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <Input
                  value={loiForm.subject}
                  onChange={(e) => setLoiForm({ ...loiForm, subject: e.target.value })}
                  placeholder="LOI Subject"
                />
              </FormControl>
              <FormControl>
                <FormLabel>
                  Content <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <TextEditor
                  toolbarId="loi-content-create"
                  value={loiForm.content}
                  handleChange={(value) => {
                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                    setLoiForm({ ...loiForm, content: cleanedValue });
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Available placeholders: [Candidate Name], [Job Role], [YourName]
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateLOI}
              isLoading={isLoading}
              loadingText="Creating..."
            >
              Create LOI
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit LOI Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Letter of Intent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Email Template (Optional)</FormLabel>
                <Select
                  placeholder={isTemplatesLoading ? "Loading templates..." : "Select an email template"}
                  value={loiForm.emailTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  isDisabled={isTemplatesLoading}
                >
                  <option value="">None - Use custom content</option>
                  {emailTemplates && emailTemplates.length > 0 ? (
                    emailTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {template.name} {template.category ? `(${template.category})` : ''}
                      </option>
                    ))
                  ) : (
                    !isTemplatesLoading && <option value="" disabled>No templates available</option>
                  )}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {isTemplatesLoading
                    ? "Loading templates..."
                    : emailTemplates.length > 0
                      ? `Selecting a template will auto-fill subject and content with placeholders replaced (${emailTemplates.length} templates available)`
                      : "No email templates available. Create templates in the Email Templates section."}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel>
                  Subject <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <Input
                  value={loiForm.subject || loi?.subject || ''}
                  onChange={(e) => setLoiForm({ ...loiForm, subject: e.target.value })}
                  placeholder="LOI Subject"
                />
              </FormControl>
              <FormControl>
                <FormLabel>
                  Content <span style={{ color: 'red' }}>*</span>
                </FormLabel>
                <TextEditor
                  toolbarId="loi-content-edit"
                  value={loiForm.content || loi?.content || ''}
                  handleChange={(value) => {
                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                    setLoiForm({ ...loiForm, content: cleanedValue });
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Available placeholders: [Candidate Name], [Job Role], [YourName]
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleUpdateLOI}>
              Update LOI
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>LOI Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
              <Text fontWeight="bold" mb={2}>
                Subject: {loiForm.subject}
              </Text>
              <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(loiForm.content) }} />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPreviewClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Send LOI Confirmation Modal */}
      <Modal isOpen={isSendConfirmOpen} onClose={onSendConfirmClose} isCentered closeOnOverlayClick={!isSendingLOI}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send LOI</ModalHeader>
          <ModalCloseButton isDisabled={isSendingLOI} />
          <ModalBody>
            {isSendingLOI ? (
              <Box textAlign="center" py={4}>
                <Spinner size="xl" color="blue.500" thickness="4px" speed="0.65s" />
                <Text mt={4} fontSize="md" color="gray.600">
                  Sending LOI to candidate...
                </Text>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Please wait while we send the email.
                </Text>
              </Box>
            ) : (
              <Text>
                Are you sure you want to send this LOI to the candidate? This action cannot be undone.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={onSendConfirmClose}
              variant="outline"
              isDisabled={isSendingLOI}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSendLOI}
              isLoading={isSendingLOI}
              loadingText="Sending..."
              isDisabled={isSendingLOI}
            >
              Yes, Send LOI
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Withdraw LOI Confirmation Modal */}
      <Modal isOpen={isWithdrawConfirmOpen} onClose={onWithdrawConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Withdraw LOI</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to withdraw this LOI? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onWithdrawConfirmClose} variant="outline">
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleWithdrawLOI}>
              Yes, Withdraw LOI
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LOITab;
